import React, { useEffect, useState } from 'react';
import {
  Table,
  Card,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  InputNumber,
  message,
  Tooltip,
  Drawer,
  Descriptions,
  Statistic,
  Row,
  Col,
  Tabs,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  DollarOutlined,
  BankOutlined,
  UserOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { get, post } from '../../utils/api';
import { formatDateTime, formatAmount, maskPhone } from '../../utils/index';

const { TabPane } = Tabs;
const { Option } = Select;

const KycPaymentManagement = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingList, setPendingList] = useState([]);
  const [paidList, setPaidList] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [paidModalVisible, setPaidModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    keyword: '',
    status: '',
  });
  const [form] = Form.useForm();
  const [paidForm] = Form.useForm();

  // 获取统计信息
  const fetchStats = async () => {
    try {
      const response = await get('/admin/kyc/payments/stats');
      if (response.code === 200) {
        setStats(response.data || {});
      }
    } catch (error) {
      console.error('获取统计信息失败:', error);
    }
  };

  // 获取待打款列表
  const fetchPendingList = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        pageSize: pagination.pageSize,
        keyword: filters.keyword,
      };
      const response = await get('/admin/kyc/payments/pending', params);
      if (response.code === 200) {
        setPendingList(response.data.list || []);
        setPagination({
          ...pagination,
          total: response.data.total || 0,
        });
      }
    } catch (error) {
      message.error('获取待打款列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取已打款列表
  const fetchPaidList = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        pageSize: pagination.pageSize,
        status: filters.status,
        keyword: filters.keyword,
      };
      const response = await get('/admin/kyc/payments/paid', params);
      if (response.code === 200) {
        setPaidList(response.data.list || []);
        setPagination({
          ...pagination,
          total: response.data.total || 0,
        });
      }
    } catch (error) {
      message.error('获取已打款列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取订单详情
  const fetchOrderDetail = async (id) => {
    try {
      const response = await get(`/admin/kyc/payments/${id}`);
      if (response.code === 200) {
        setSelectedRecord(response.data);
        setDetailVisible(true);
      }
    } catch (error) {
      message.error('获取订单详情失败');
    }
  };

  // 确认打款
  const handleConfirmPaid = async (values) => {
    try {
      const params = {
        amount: values.amount,
        remark: values.remark,
      };
      const response = await post(`/admin/kyc/payments/${selectedRecord.id}/paid`, params);
      if (response.code === 200) {
        message.success('打款确认成功');
        setPaidModalVisible(false);
        paidForm.resetFields();
        if (activeTab === 'pending') {
          fetchPendingList();
        } else {
          fetchPaidList();
        }
        fetchStats();
      }
    } catch (error) {
      message.error(error.message || '打款确认失败');
    }
  };

  // 搜索处理
  const handleSearch = (values) => {
    setFilters({
      ...filters,
      keyword: values.keyword || '',
      status: values.status || '',
    });
    setPagination({
      ...pagination,
      current: 1,
    });
  };

  // 打款操作
  const handlePaid = async (record) => {
    try {
      const response = await get(`/admin/kyc/payments/${record.id}`);
      if (response.code === 200) {
        setSelectedRecord(response.data);
        paidForm.setFieldsValue({
          amount: response.data.amount,
          remark: '',
        });
        setPaidModalVisible(true);
      }
    } catch (error) {
      message.error('获取订单信息失败');
    }
  };

  // 查看详情
  const handleViewDetail = (record) => {
    fetchOrderDetail(record.id);
  };

  // 刷新
  const handleRefresh = () => {
    if (activeTab === 'pending') {
      fetchPendingList();
    } else {
      fetchPaidList();
    }
    fetchStats();
  };

  // Tab切换
  const handleTabChange = (key) => {
    setActiveTab(key);
    setPagination({
      ...pagination,
      current: 1,
    });
  };

  // 表格变化处理
  const handleTableChange = (newPagination) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'pending') {
      fetchPendingList();
    } else {
      fetchPaidList();
    }
  }, [activeTab, pagination.current, pagination.pageSize, filters]);

  // 待打款列表列定义
  const pendingColumns = [
    {
      title: '订单号',
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 200,
    },
    {
      title: '用户信息',
      key: 'user',
      width: 150,
      render: (_, record) => (
        <div>
          <div>
            <UserOutlined /> {maskPhone(record.userPhone)}
          </div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            ID: {record.userId}
          </div>
        </div>
      ),
    },
    {
      title: '银行卡信息',
      key: 'bank',
      width: 250,
      render: (_, record) => {
        const account = record.paymentAccount;
        if (!account) return '-';
        return (
          <div>
            <div>
              <BankOutlined /> {account.bankName}
            </div>
            <div style={{ fontSize: '12px', color: '#999' }}>
              {account.bankAccount} ({account.accountHolder})
            </div>
            {account.accountName && (
              <div style={{ fontSize: '12px', color: '#999' }}>
                账户名: {account.accountName}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '打款金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount) => (
        <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
          {formatAmount(amount)}
        </span>
      ),
    },
    {
      title: '申请时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date) => formatDateTime(date),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>
          <Tooltip title="确认打款">
            <Button
              type="primary"
              icon={<DollarOutlined />}
              onClick={() => handlePaid(record)}
            >
              打款
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // 已打款列表列定义
  const paidColumns = [
    {
      title: '订单号',
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 200,
    },
    {
      title: '用户信息',
      key: 'user',
      width: 150,
      render: (_, record) => (
        <div>
          <div>
            <UserOutlined /> {maskPhone(record.userPhone)}
          </div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            ID: {record.userId}
          </div>
        </div>
      ),
    },
    {
      title: '银行卡信息',
      key: 'bank',
      width: 250,
      render: (_, record) => {
        const account = record.paymentAccount;
        if (!account) return '-';
        return (
          <div>
            <div>
              <BankOutlined /> {account.bankName}
            </div>
            <div style={{ fontSize: '12px', color: '#999' }}>
              {account.bankAccount} ({account.accountHolder})
            </div>
          </div>
        );
      },
    },
    {
      title: '打款金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount) => (
        <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
          {formatAmount(amount)}
        </span>
      ),
    },
    {
      title: '打款时间',
      dataIndex: 'paidAt',
      key: 'paidAt',
      width: 180,
      render: (date) => date ? formatDateTime(date) : '-',
    },
    {
      title: '打款操作人',
      key: 'paidBy',
      width: 120,
      render: (_, record) => record.paidBy ? record.paidBy.username : '-',
    },
    {
      title: '验证状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const statusMap = {
          1: { text: '已打款', color: 'blue' },
          2: { text: '验证成功', color: 'green' },
          3: { text: '验证失败', color: 'red' },
          4: { text: '已过期', color: 'default' },
        };
        const statusInfo = statusMap[status] || { text: '未知', color: 'default' };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="查看详情">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">KYC打款管理</h1>
        <p className="page-description">管理用户实名认证打款申请和处理</p>
      </div>

      {/* 统计信息 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="待打款"
              value={stats.pendingCount || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="已打款"
              value={stats.paidCount || 0}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="验证成功"
              value={stats.verifiedCount || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="今日打款"
              value={stats.todayPaidCount || 0}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane
            tab={
              <span>
                <ClockCircleOutlined />
                待打款 ({stats.pendingCount || 0})
              </span>
            }
            key="pending"
          >
            <div style={{ marginBottom: 16 }}>
              <Form
                layout="inline"
                onFinish={handleSearch}
                initialValues={filters}
              >
                <Form.Item name="keyword">
                  <Input
                    placeholder="搜索订单号/用户手机号"
                    prefix={<SearchOutlined />}
                    style={{ width: 250 }}
                    allowClear
                  />
                </Form.Item>
                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit">
                      搜索
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                      刷新
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </div>

            <Table
              columns={pendingColumns}
              dataSource={pendingList}
              rowKey="id"
              loading={loading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条`,
              }}
              onChange={handleTableChange}
              scroll={{ x: 1200 }}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <DollarOutlined />
                已打款 ({stats.paidCount || 0})
              </span>
            }
            key="paid"
          >
            <div style={{ marginBottom: 16 }}>
              <Form
                layout="inline"
                onFinish={handleSearch}
                initialValues={filters}
              >
                <Form.Item name="keyword">
                  <Input
                    placeholder="搜索订单号/用户手机号"
                    prefix={<SearchOutlined />}
                    style={{ width: 250 }}
                    allowClear
                  />
                </Form.Item>
                <Form.Item name="status">
                  <Select placeholder="验证状态" style={{ width: 150 }} allowClear>
                    <Option value="verified">验证成功</Option>
                    <Option value="failed">验证失败</Option>
                    <Option value="expired">已过期</Option>
                  </Select>
                </Form.Item>
                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit">
                      搜索
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                      刷新
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </div>

            <Table
              columns={paidColumns}
              dataSource={paidList}
              rowKey="id"
              loading={loading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条`,
              }}
              onChange={handleTableChange}
              scroll={{ x: 1400 }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* 订单详情抽屉 */}
      <Drawer
        title="订单详情"
        placement="right"
        width={600}
        onClose={() => setDetailVisible(false)}
        open={detailVisible}
      >
        {selectedRecord && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="订单号">
              {selectedRecord.orderNo}
            </Descriptions.Item>
            <Descriptions.Item label="用户ID">
              {selectedRecord.userId}
            </Descriptions.Item>
            <Descriptions.Item label="用户手机号">
              {maskPhone(selectedRecord.userPhone)}
            </Descriptions.Item>
            {selectedRecord.paymentAccount && (
              <>
                <Descriptions.Item label="账户名称">
                  {selectedRecord.paymentAccount.accountName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="银行名称">
                  {selectedRecord.paymentAccount.bankName}
                </Descriptions.Item>
                <Descriptions.Item label="开户行">
                  {selectedRecord.paymentAccount.bankBranch || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="银行卡号">
                  {selectedRecord.paymentAccount.bankAccount}
                </Descriptions.Item>
                <Descriptions.Item label="开户名">
                  {selectedRecord.paymentAccount.accountHolder}
                </Descriptions.Item>
              </>
            )}
            <Descriptions.Item label="打款金额">
              <span style={{ color: '#1890ff', fontWeight: 'bold', fontSize: '16px' }}>
                {formatAmount(selectedRecord.amount)}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={selectedRecord.status === 0 ? 'orange' : selectedRecord.status === 1 ? 'blue' : selectedRecord.status === 2 ? 'green' : 'red'}>
                {selectedRecord.statusText}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="申请时间">
              {formatDateTime(selectedRecord.createdAt)}
            </Descriptions.Item>
            {selectedRecord.paidAt && (
              <Descriptions.Item label="打款时间">
                {formatDateTime(selectedRecord.paidAt)}
              </Descriptions.Item>
            )}
            {selectedRecord.paidBy && (
              <Descriptions.Item label="打款操作人">
                {selectedRecord.paidBy.username}
              </Descriptions.Item>
            )}
            {selectedRecord.paidRemark && (
              <Descriptions.Item label="打款备注">
                {selectedRecord.paidRemark}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="过期时间">
              {formatDateTime(selectedRecord.expiresAt)}
            </Descriptions.Item>
            {selectedRecord.verifiedAt && (
              <Descriptions.Item label="验证时间">
                {formatDateTime(selectedRecord.verifiedAt)}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="验证次数">
              {selectedRecord.verifyAttempts || 0} / {selectedRecord.maxVerifyAttempts || 3}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>

      {/* 确认打款弹窗 */}
      <Modal
        title="确认打款"
        open={paidModalVisible}
        onOk={() => paidForm.submit()}
        onCancel={() => {
          setPaidModalVisible(false);
          paidForm.resetFields();
        }}
        okText="确认打款"
        cancelText="取消"
        width={600}
      >
        {selectedRecord && (
          <div style={{ marginBottom: 16 }}>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="订单号">
                {selectedRecord.orderNo}
              </Descriptions.Item>
              <Descriptions.Item label="用户手机号">
                {maskPhone(selectedRecord.userPhone)}
              </Descriptions.Item>
              {selectedRecord.paymentAccount && (
                <>
                  <Descriptions.Item label="银行名称">
                    {selectedRecord.paymentAccount.bankName}
                  </Descriptions.Item>
                  <Descriptions.Item label="开户行">
                    {selectedRecord.paymentAccount.bankBranch || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="银行卡号">
                    {selectedRecord.paymentAccount.bankAccount}
                  </Descriptions.Item>
                  <Descriptions.Item label="开户名">
                    {selectedRecord.paymentAccount.accountHolder}
                  </Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="系统生成金额">
                <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                  {formatAmount(selectedRecord.amount)}
                </span>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}

        <Form
          form={paidForm}
          layout="vertical"
          onFinish={handleConfirmPaid}
        >
          <Form.Item
            label="实际打款金额"
            name="amount"
            rules={[
              { required: true, message: '请输入打款金额' },
              { type: 'number', min: 0.01, max: 1.00, message: '金额必须在0.01～1.00元之间' },
            ]}
            extra="系统已生成默认金额，您可以根据实际情况修改（0.01～1.00元）"
          >
            <InputNumber
              style={{ width: '100%' }}
              prefix="¥"
              precision={2}
              min={0.01}
              max={1.00}
              step={0.01}
            />
          </Form.Item>
          <Form.Item
            label="打款备注"
            name="remark"
            extra="可选，记录打款相关信息"
          >
            <Input.TextArea rows={3} placeholder="请输入打款备注" />
          </Form.Item>
        </Form>

        <div style={{ marginTop: 16, padding: 12, background: '#f0f2f5', borderRadius: 4 }}>
          <p style={{ margin: 0, color: '#666' }}>
            <strong>提示：</strong>请向该银行卡打款指定金额，打款完成后点击"确认打款"按钮。
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default KycPaymentManagement;

